import React, {useEffect} from 'react';

import {NucleotideType} from 'biojs/Util';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  ModifiedNucleotidesQuery,
  ModifiedNucleotidesQueryVariables,
} from 'molbio/monomers/__generated/ModifiedNucleotidesQuery';
import {MonomerGraphQL} from 'molbio/monomers/__generated/MonomerGraphQL';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {getDisplayModsForSlice} from 'molbio/monomers/ModificationVisualizationUtil';
import {
  displayAsNatural,
  getVisualSymbolForPhosphateMonomer,
} from 'molbio/monomers/ModificationVisualizationUtil';
import ModifiedBase from 'molbio/monomers/ModifiedBase';
import ModifiedPhosphate from 'molbio/monomers/ModifiedPhosphate';
import ModifiedSugar from 'molbio/monomers/ModifiedSugar';
import {
  MONOMER_QUERY,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  NucleotideComponent,
} from 'molbio/monomers/MonomerConstants';
import {MODIFIED_NUCLEOTIDES_QUERY} from 'molbio/monomers/NucleotidesTab';

import {ModifiedNucleotideGraphQL} from '../../../../graphql-fragments/modbio/__generated/ModifiedNucleotideGraphQL';
import ComponentWrapper from './ComponentWrapper';
import {widthBetweenLetters} from './LinearMapUtil';

// Nucleotide letters disappear on min zoom. This is the depth at which bars appear approximately where
// nucleotides used to be without colliding with other sequence map features
export const MAXIMUM_LETTER_WIDTH_FOR_BARS = 6;
export const MAXIMUM_LETTER_WIDTH_FOR_DOTS = 24;
const MAXIMUM_ZOOM_FOR_SCALING = 12;
// Dot view formatting is parameterized off of BASE_RADIUS and MARGIN_FACTOR. BASE_RADIUS controls the size
// of base, sugar, and phosphate symbols. MARGIN_FACTOR controls the space between these symbols.
const BASE_RADIUS = 3.5;
const MARGIN_FACTOR = 1.2;
const TOP_MARGIN = 1;
const SUGAR_RADIUS = BASE_RADIUS * 0.75;
const PHOSPHATE_SIZE = 2 * BASE_RADIUS;

interface ModifiedNucleotideProps {
  layout: AllPolymerLayouts['modifiedNucleotideSummary'];
  redraw: (boxHeight: number) => void;
  sliceEnd: number;
  sliceStart: number;
}
export interface ModifiedNucleotideSummaryProps extends ModifiedNucleotideProps {
  sequenceId: string;
  xFn: (index: number) => number;
  sliceBases: string;
}

interface ModifiedNucleotideViewAtLetterWidthProps extends ModifiedNucleotideProps {
  letterWidth: number;
  modifiedNucleotides: Array<ModifiedNucleotideAttributes>;
  monomers: Array<MonomerGraphQL>;
  nucleotideType: NucleotideType;
  isOligo: boolean;
}

interface ModifiedNucleotideViewProps
  extends Pick<
    ModifiedNucleotideViewAtLetterWidthProps,
    'isOligo' | 'layout' | 'letterWidth' | 'monomers' | 'nucleotideType' | 'sliceStart'
  > {
  seqSliceMods: Array<ModifiedNucleotideAttributes>;
}

export interface ModifiedNucleotideQueryProps extends ModifiedNucleotideProps {
  letterWidth: number;
  sequenceId: string;
  sliceBases: string;
}

type ModifiedNucleotideBarViewProps = Pick<
  ModifiedNucleotideViewProps,
  'letterWidth' | 'sliceStart' | 'seqSliceMods' | 'layout'
>;

interface ModHeightOffsetProps {
  children: JSX.Element;
  offsetY: number;
}

function letterCenterX(letterWidth: number, sliceStart: number, i: number): number {
  return (i - sliceStart) * letterWidth + 0.5 * letterWidth;
}

function getScaleFactor(letterWidth: number): number {
  // letters are 'scaling in' from letterWidth 6 to 12
  const sizeScale = 11;
  return Math.min(letterWidth / sizeScale, 1);
}

export function getBoxHeightWithMods(letterWidth: number): number {
  // ensure boxHeight is preserved after letters disappear. Add 4 so that bars appear where letters were.
  if (letterWidth < MAXIMUM_LETTER_WIDTH_FOR_BARS) {
    return letterWidth + 4;
  }
  const baseRadius = BASE_RADIUS * getScaleFactor(letterWidth);
  const phosphateSize = PHOSPHATE_SIZE * getScaleFactor(letterWidth);

  // Sugar radius is equal to bases after strokeWidth is applied. Add additional margin factor to create
  // margin on top.
  return phosphateSize + MARGIN_FACTOR * baseRadius * 2 + MARGIN_FACTOR * baseRadius * 2 + TOP_MARGIN;
}

function getYOffsetForMod(
  modDict: {[key: string]: boolean},
  modType: NucleotideComponent,
  letterWidth: number
): number {
  /**
   * Returns Y-offset values for individual mod components so that they appear in the correct (top-to-bottom)
   * order of base, sugar, phosphate.
   */
  const baseRadius = BASE_RADIUS * getScaleFactor(letterWidth);
  const slotHeight = baseRadius * 2 * MARGIN_FACTOR;

  const phosphatePresent = modDict[NucleotideComponent.PHOSPHATE];
  const sugarPresent = modDict[NucleotideComponent.SUGAR];

  // Initialize with top side margin.
  let offset = TOP_MARGIN;
  if (modType === NucleotideComponent.BASE) {
    // If no sugar, move down one slot
    if (!sugarPresent) {
      offset += slotHeight;
    }
    // If no phosphate, move down one slot
    if (!phosphatePresent) {
      offset += slotHeight;
    }
  }

  if (modType === NucleotideComponent.SUGAR) {
    // Always move down one slot
    offset += slotHeight;
    // If no phosphate, move down another
    if (!phosphatePresent) {
      offset += slotHeight;
    }
  }

  if (modType === NucleotideComponent.PHOSPHATE) {
    // Always move down into last slot
    offset += slotHeight * 2;
  }

  return offset;
}

function getModificationsAtPosition(
  mod: ModifiedNucleotideGraphQL,
  monomers: Array<MonomerGraphQL>,
  nucleotideType: NucleotideType,
  isOligo: boolean
): {[key in NucleotideComponent]?: boolean} {
  const modsPresentDict: {[key in NucleotideComponent]?: boolean} = {};
  modsPresentDict[NucleotideComponent.PHOSPHATE] = !displayAsNatural(
    mod.phosphateMonomerId,
    monomers,
    nucleotideType,
    isOligo
  );
  modsPresentDict[NucleotideComponent.SUGAR] = !displayAsNatural(
    mod.sugarMonomerId,
    monomers,
    nucleotideType,
    isOligo
  );
  modsPresentDict[NucleotideComponent.BASE] = !displayAsNatural(
    mod.baseMonomerId,
    monomers,
    nucleotideType,
    isOligo
  );
  return modsPresentDict;
}

function getPhosphateXOffset(letterWidth: number): number {
  /**
   * Phosphate symbols are drawn from the top left corner. A negative x-offset is needed to move them to the left
   * and center them over nucleotide letters.
   *
   * To keep phosphates aligned with nucleotide letters, the x-offset decreases proportionately as letters
   * scale in from letterWidth 6 to 12.
   */
  const mediumZoomOffset = -PHOSPHATE_SIZE / 2; // lower values move phosphates further to the left
  const xAxisZoomScale = 1; // higher values move P to right while zooming
  return Math.max(
    mediumZoomOffset,
    mediumZoomOffset * ((letterWidth + xAxisZoomScale) / MAXIMUM_ZOOM_FOR_SCALING)
  );
}

function ModHeightOffset({offsetY, children}: ModHeightOffsetProps): JSX.Element | null {
  return <g transform={`translate(0, ${offsetY})`}>{children}</g>;
}

function ModifiedNucleotideDotView({
  isOligo,
  letterWidth,
  sliceStart,
  monomers,
  seqSliceMods,
  layout,
  nucleotideType,
}: ModifiedNucleotideViewProps): JSX.Element | null {
  return (
    <svg>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
        <g>
          {seqSliceMods.map((mod: ModifiedNucleotideGraphQL, _) => {
            const modsPresentAtPosition = getModificationsAtPosition(mod, monomers, nucleotideType, isOligo);
            const scaleFactor = getScaleFactor(letterWidth);
            const baseMonomer = monomers.find((monomer) => monomer.id === mod.baseMonomerId);
            const baseMonomerRadius = BASE_RADIUS * scaleFactor;
            const sugarMonomer = monomers.find((monomer) => monomer.id === mod.sugarMonomerId);
            const sugarMonomerRadius = SUGAR_RADIUS * scaleFactor;
            const phosphateMonomer = monomers.find((monomer) => monomer.id === mod.phosphateMonomerId);
            return (
              <g
                key={`${mod.position}-${mod.sugarMonomerId}-${mod.baseMonomerId}-${mod.phosphateMonomerId}`}
                // The translate below subtracts 0.25 from the X-axis to  move letters to the left
                // so that they are centered over capital As.
                transform={`translate(${letterCenterX(letterWidth, sliceStart, mod.position) - 0.25})`}
              >
                {modsPresentAtPosition[NucleotideComponent.PHOSPHATE] && (
                  <ModifiedPhosphate
                    size={PHOSPHATE_SIZE * scaleFactor}
                    offsetX={getPhosphateXOffset(letterWidth)}
                    offsetY={getYOffsetForMod(
                      modsPresentAtPosition,
                      NucleotideComponent.PHOSPHATE,
                      letterWidth
                    )}
                    symbol={getVisualSymbolForPhosphateMonomer(phosphateMonomer)}
                  />
                )}
                {modsPresentAtPosition[NucleotideComponent.SUGAR] && (
                  <ModHeightOffset
                    offsetY={getYOffsetForMod(modsPresentAtPosition, NucleotideComponent.SUGAR, letterWidth)}
                  >
                    <ModifiedSugar
                      stroke={sugarMonomer?.color ?? DEFAULT_MODIFIED_SUGAR_COLOR}
                      radius={sugarMonomerRadius}
                      strokeWidth={(baseMonomerRadius - sugarMonomerRadius) * 2}
                      cy={baseMonomerRadius} // accounts for sugarMonomerRadius + strokeWidth
                    />
                  </ModHeightOffset>
                )}
                {modsPresentAtPosition[NucleotideComponent.BASE] && (
                  <ModHeightOffset
                    offsetY={getYOffsetForMod(modsPresentAtPosition, NucleotideComponent.BASE, letterWidth)}
                  >
                    <ModifiedBase
                      fill={baseMonomer?.color ?? DEFAULT_MODIFIED_SUGAR_COLOR}
                      radius={baseMonomerRadius}
                      cy={baseMonomerRadius}
                    />
                  </ModHeightOffset>
                )}
              </g>
            );
          })}
        </g>
      </ComponentWrapper>
    </svg>
  );
}

function ModifiedNucleotideBarView({
  layout,
  letterWidth,
  seqSliceMods,
  sliceStart,
}: ModifiedNucleotideBarViewProps): JSX.Element | null {
  return (
    <svg>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
        <g>
          {seqSliceMods.map((mod: ModifiedNucleotideGraphQL, _) => {
            const offsetX = letterCenterX(letterWidth, sliceStart, mod.position) - letterWidth / 2;
            return (
              <g
                key={`${mod.position}-${mod.sugarMonomerId}-${mod.baseMonomerId}-${mod.phosphateMonomerId}`}
                transform={`translate(${offsetX}, ${getBoxHeightWithMods(letterWidth)})`}
              >
                <rect
                  width={letterWidth}
                  height={3}
                  stroke={DEFAULT_MODIFIED_SUGAR_COLOR}
                  fill={DEFAULT_MODIFIED_SUGAR_COLOR}
                />
              </g>
            );
          })}
        </g>
      </ComponentWrapper>
    </svg>
  );
}

function ModifiedNucleotideViewAtLetterWidth({
  isOligo,
  layout,
  letterWidth,
  modifiedNucleotides,
  monomers,
  nucleotideType,
  redraw,
  sliceEnd,
  sliceStart,
}: ModifiedNucleotideViewAtLetterWidthProps): JSX.Element | null {
  const seqSliceMods = modifiedNucleotides.filter(
    (mod: ModifiedNucleotideGraphQL) => mod.position >= sliceStart && mod.position < sliceEnd
  );
  const modsPresent = seqSliceMods.length > 0;
  const modsVisible = letterWidth <= MAXIMUM_LETTER_WIDTH_FOR_DOTS;

  useEffect(() => {
    if (modsPresent && modsVisible) {
      redraw(getBoxHeightWithMods(letterWidth));
    } else {
      redraw(0);
    }
  }, [modsPresent, modsVisible, letterWidth, redraw]);

  if (!modsVisible) {
    return null;
  }

  // round up to catch edge case in letterWidth
  if (Math.round(letterWidth) >= MAXIMUM_LETTER_WIDTH_FOR_BARS) {
    return (
      <ModifiedNucleotideDotView
        isOligo={isOligo}
        letterWidth={letterWidth}
        sliceStart={sliceStart}
        monomers={monomers}
        seqSliceMods={seqSliceMods}
        layout={layout}
        nucleotideType={nucleotideType}
      />
    );
  } else {
    return (
      <ModifiedNucleotideBarView
        letterWidth={letterWidth}
        sliceStart={sliceStart}
        seqSliceMods={seqSliceMods}
        layout={layout}
      />
    );
  }
}

// exported for unit testing
export function QueryingModifiedNucleotideViewAtLetterWidth({
  layout,
  letterWidth,
  redraw,
  sequenceId,
  sliceEnd,
  sliceStart,
  sliceBases,
}: ModifiedNucleotideQueryProps): JSX.Element | null {
  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={({refetch}) => null}>
      {({monomers}) => (
        // Hack: fetch monomers first to populate the cache, to make MODIFIED_NUCLEOTIDES_QUERY work
        <BenchlingQuery<ModifiedNucleotidesQuery, ModifiedNucleotidesQueryVariables>
          query={MODIFIED_NUCLEOTIDES_QUERY}
          renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
          variables={{sequenceId}}
          fetchPolicy="cache-only"
        >
          {(data) => {
            const seqMods = data.componentSequence!.modifiedNucleotides;
            const displayMods = getDisplayModsForSlice(
              monomers,
              data.componentSequence!.nucleotideType,
              sliceBases,
              seqMods,
              sliceStart
            );
            return (
              <ModifiedNucleotideViewAtLetterWidth
                isOligo={data.componentSequence!.type === SequenceType.oligo}
                layout={layout}
                letterWidth={letterWidth}
                modifiedNucleotides={displayMods}
                monomers={monomers}
                nucleotideType={data.componentSequence!.nucleotideType}
                redraw={redraw}
                sliceEnd={sliceEnd}
                sliceStart={sliceStart}
              />
            );
          }}
        </BenchlingQuery>
      )}
    </BenchlingQuery>
  );
}

export default function ModifiedNucleotideSummary({
  xFn,
  sliceStart,
  sliceEnd,
  sequenceId,
  layout,
  redraw,
  sliceBases,
}: ModifiedNucleotideSummaryProps): JSX.Element | null {
  const letterWidth = widthBetweenLetters(xFn, sliceStart);
  return (
    <QueryingModifiedNucleotideViewAtLetterWidth
      layout={layout}
      letterWidth={letterWidth}
      redraw={redraw}
      sequenceId={sequenceId}
      sliceEnd={sliceEnd}
      sliceStart={sliceStart}
      sliceBases={sliceBases}
    />
  );
}
